import React from 'react';
import { ButtonGroup, Button, Form, Row, Col } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const FilterControls = ({ 
  currentFilter, 
  onFilterChange, 
  searchQuery, 
  onSearchChange, 
  searchId, // Nueva Prop
  onSearchIdChange, // Nueva Prop
  startDate, 
  setStartDate, 
  endDate, 
  setEndDate, 
  sortByCompletionDate, 
  onSortToggle,
  filteredData,
  currentTypeFilter,
  onTypeFilterChange,
  currentCategoryFilter,
  onCategoryFilterChange,
  currentSubcategoryFilter, // Nueva Prop
  onSubcategoryFilterChange // Nueva Prop
}) => {

  const SUB_CATEGORIES_EXPENSE = [
    "Arriendo", "Cafetería y Snacks", "Combustible", "Comidas de Negocios", 
    "Comisiones Bancarias", "Equipos Tecnológicos", "Estacionamientos", 
    "Eventos Corporativos", "Insumos de Aseo", "Insumos de Oficina", "Internet", 
    "Lavado de Vehículos", "Legales y Notaría", "Mantenimiento Vehículos", 
    "Otros", "Patentes y Permisos", "Peajes y TAG", "Previred", 
    "Redes Sociales", "Servicios Básicos", "Suscripciones de Software", 
    "Telefonía Móvil/Fija", "Viajes de Negocios"
  ];

  const SUB_CATEGORIES_FINANCIAL = [
    "Compra Divisas (USD)", "Devolución de Gasto", "Inversión DAP", "Préstamo / Socios", "Remesa Internacional", "Transferencia Interna"
  ];

  const SUB_CATEGORIES_PAYROLL = [
    "Bonos", "HHEE", "HHEE Domingo", "HHEE Sábado", "Previred", "Sueldo", "Viáticos"
  ];

  const getEerrStatusText = (transaction) => {
    const { status, category, subcategory, type, amount, dap_interest, cc_commission, penalizaciones_booking, accrual_date, date } = transaction;
    const isCompleted = status === 'completado';

    if (!isCompleted && status !== 'incobrable') return 'Pendiente';

    const catLower = category ? category.toLowerCase() : '';
    if (['particulares', 'conductores'].includes(catLower)) return 'Excluido';

    const effectiveDateString = accrual_date || date;
    const effectiveDate = new Date(effectiveDateString + 'T00:00:00');
    const monthYear = effectiveDate.toLocaleDateString('es-CL', { month: 'short', year: 'numeric' });

    let netImpact = 0;
    const subcatLower = subcategory ? subcategory.toLowerCase() : '';
    
    // Fix: Local INCOMES do NOT add to EERR (Sales come from External Sheet), EXCEPT DAP and Devolución de Gasto
    if (type === 'egreso') {
        netImpact -= amount;
    } else if (type === 'ingreso') {
        if (catLower === 'movimiento financiero' || subcatLower.includes('devoluc')) {
            netImpact += amount;
        }
    }

    if (dap_interest) netImpact += dap_interest;
    if (cc_commission) netImpact -= cc_commission;
    if (penalizaciones_booking) netImpact -= penalizaciones_booking;

    let attribution = '';
    if (status === 'incobrable') attribution = 'Castigo';
    else if (['remuneraciones', 'salario'].includes(catLower)) attribution = 'Remun.';
    else if (catLower.includes('hhee')) attribution = 'HHEE';
    else if (catLower === 'impuestos directos') attribution = 'Impuestos';
    else if (type === 'ingreso') {
        if (dap_interest > 0) attribution = 'DAP';
        else if (catLower === 'movimiento financiero' || subcatLower.includes('devoluc')) attribution = 'Otros Ing.';
        else attribution = 'Flujo'; 
    } else {
        attribution = 'Gastos'; 
    }

    if (netImpact > 0) return `(+) ${attribution} (${monthYear})`;
    if (netImpact < 0) return `(-) ${attribution} (${monthYear})`;
    if (type === 'ingreso' && netImpact === 0) return 'Flujo (No EERR)';

    return 'Neutro';
  };

  const downloadExcel = () => {
    if (!filteredData || filteredData.length === 0) return;

    // Preparar los datos mapeados exactamente como los queremos en el Excel
    const dataToExport = filteredData.map(t => ({
      "ID": t.id,
      "Fecha": t.date,
      "F. Imputación": t.accrual_date || '',
      "Tipo": t.type ? t.type.toUpperCase() : '',
      "Categoría": t.category,
      "Subcategoría": t.subcategory || '-',
      "Entidad": t.entity_name || '',
      "Descripción": t.description,
      "Monto": t.amount,
      "Impacto en Saldo": t.balanceImpact || 0,
      "EERR Status": getEerrStatusText(t),
      "Boleta/Factura": t.invoice_number || '',
      "Solicitud/Liquidación": t.request_id || '',
      "Estado": t.status,
      "Observaciones": t.notes || '',
      "Comisión T/C": t.cc_commission || 0,
      "Intereses DAP": t.dap_interest || 0,
      "Penalizaciones Booking": t.penalizaciones_booking || 0,
      "Fecha Finalización": t.completion_date || '',
      "Saldo Acumulado": t.runningBalance || 0
    }));

    // Crear el libro de Excel
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transacciones");

    // Generar archivo y descargar
    const fileName = `transacciones_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <>
      <Row className="mb-3 align-items-center">
        {/* ID Search (Small) */}
        <Col md={1}>
          <Form.Control
            type="text"
            placeholder="ID"
            value={searchId || ''}
            onChange={(e) => onSearchIdChange(e.target.value.replace(/\D/g, ''))} // Solo números
            title="Buscar por ID exacto"
            className="text-center fw-bold border-primary"
          />
        </Col>

        {/* Search Bar */}
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Buscar por descripción, categoría, monto, conductor, etc..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </Col>

        {/* Date Filters */}
        <Col md={3}>
          <Form.Group controlId="filterStartDate">
            <Form.Label className="visually-hidden">Fecha Inicio</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Fecha de Inicio"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="filterEndDate">
            <Form.Label className="visually-hidden">Fecha Fin</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              title="Fecha de Fin"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12} className="d-flex align-items-center gap-2 flex-wrap">
          {/* Type Filter */}
          <Form.Select 
            style={{ width: 'auto' }} 
            value={currentTypeFilter} 
            onChange={(e) => onTypeFilterChange(e.target.value)}
          >
            <option value="all">Todos los Tipos</option>
            <option value="ingreso">Ingreso</option>
            <option value="egreso">Egreso</option>
          </Form.Select>

          {/* Category Filter */}
          <Form.Select 
            style={{ width: 'auto' }} 
            value={currentCategoryFilter || ''} 
            onChange={(e) => {
                onCategoryFilterChange(e.target.value);
                if (onSubcategoryFilterChange) onSubcategoryFilterChange(''); // Reset subcat when cat changes
            }}
          >
            <option value="">Todas las Categorías</option>
            <option>Remuneraciones</option>
            <option>Gasto</option>
            <option>Movimiento Financiero</option>
            <option>Servicios</option>
            <option>Transporte</option>
            <option>Comida</option>
            <option>Conductores</option>
            <option>Particulares</option>
            <option>Impuestos Directos</option>
            <option>Otros</option>
          </Form.Select>

          {/* Subcategory Filter (Dynamic) */}
          {['Gasto', 'Movimiento Financiero', 'Remuneraciones'].includes(currentCategoryFilter) && onSubcategoryFilterChange && (
             <Form.Select 
                style={{ width: 'auto', borderColor: '#0dcaf0' }} 
                value={currentSubcategoryFilter || ''} 
                onChange={(e) => onSubcategoryFilterChange(e.target.value)}
            >
                <option value="">Todas las Subcategorías</option>
                {currentCategoryFilter === 'Gasto' && SUB_CATEGORIES_EXPENSE.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                {currentCategoryFilter === 'Movimiento Financiero' && SUB_CATEGORIES_FINANCIAL.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                {currentCategoryFilter === 'Remuneraciones' && SUB_CATEGORIES_PAYROLL.map(sub => <option key={sub} value={sub}>{sub}</option>)}
            </Form.Select>
          )}

          {/* Filter Buttons */}
          <ButtonGroup>
            <Button 
              variant={currentFilter === 'all' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('all')}
            >
              Todas
            </Button>
            <Button 
              variant={currentFilter === 'pending' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('pending')}
            >
              Pendientes
            </Button>
            <Button 
              variant={currentFilter === 'esperando oc/hes' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('esperando oc/hes')}
            >
              Esperando OC/HES
            </Button>
            <Button 
              variant={currentFilter === 'overdue' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('overdue')}
            >
              Vencidas
            </Button>
            <Button 
              variant={currentFilter === 'por procesar' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('por procesar')}
            >
              Por Procesar
            </Button>
            <Button 
              variant={currentFilter === 'today' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('today')}
            >
              Hoy
            </Button>
            <Button 
              variant={currentFilter === 'completado' ? 'primary' : 'outline-primary'}
              onClick={() => onFilterChange('completado')}
            >
              Completadas
            </Button>
          </ButtonGroup>

          {/* Sort by completion date button - visible only when 'completado' filter is active */}
          {currentFilter === 'completado' && (
            <Button
              variant={sortByCompletionDate ? 'secondary' : 'outline-secondary'}
              onClick={onSortToggle}
            >
              Ordenar por Finalización
            </Button>
          )}

          {/* Excel Download Button */}
          {filteredData && filteredData.length > 0 && (
            <Button 
              variant="success" 
              className="ms-auto" 
              onClick={downloadExcel}
            >
              📥 Descargar Excel
            </Button>
          )}

        </Col>
      </Row>
    </>
  );
};

export default FilterControls;